/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import { Text, BrandColors, LinkButton, Tag, ActionButton } from '@blend/enterprise-components';

import { SelectInput } from '@blend/consumer-components';
import { State } from '../../../../../redux';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { NotaryAPI } from '../../../../../api/notary/types';
import { RoomStatus } from '../../../../../shared/constants/room';
import { ActiveCallStatuses, CallStatus } from '../../../../../shared/constants/call';
import { notaryAPI as notaryAPIObj } from '../../../../../api/notary';
import { NotificationItem } from '../../../../../components/common/Notifications/NotificationItem';
import { VideoProviderDropdown } from '../../../VideoProviderDropdown/VideoProviderDropdown';
import { addNotifications } from '../../../../../actions/pageActions';
import { updateExitModalStatus } from '../../../../../actions/signingRoomActions';
import { RoomInfoRow } from './RoomInfoRow';
import { formatLocalizedScheduleDate, formatTimeSlot } from '@utils/formatting';
import { toUpper, startCase } from '@utils/lodash';
import { diagnostics } from '@utils//diagnostics';
import { getConfig } from '@utils/configs';

import { isGroupAdmin } from '@utils/permissions';

export const styles = StyleSheet.create({
  infoRow: {
    alignItems: 'center',
    display: 'flex',
  },
  largeMargin: {
    marginBottom: 10,
  },
  smallMargin: {
    marginBottom: 8,
  },
  summaryCard: {
    ':not(:last-child)': {
      marginRight: 48,
    },
  },
  summaryCardWithoutHeader: {
    marginTop: 30,
  },
  summaryCardWithSelect: {
    marginTop: 20,
  },
  summaryContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: 25,
  },
  actionWrapper: {
    display: 'inline-block',
    marginLeft: 8,
  },
  originalContent: {
    display: 'flex',
  },
  buttonWrapper: {
    minWidth: 120,
  },
});

export namespace RoomInfoSummary {
  export interface Props {
    onReschedule?: () => void;
    onGroupOverwrite: () => void;
    onVideoOverride: () => void;
    callStatus?: string;
  }
}

export const pdtTimeZone = 'America/Los_Angeles';

export const RoomInfoSummary: React.FC<RoomInfoSummary.Props> = ({
  onReschedule,
  onGroupOverwrite,
  onVideoOverride,
  callStatus,
}) => {
  const room = useSelector((reduxState: State) => reduxState.roomDetail.room!);
  const permissions = usePermissions();
  const {
    signers,
    notaryName,
    scheduleDate,
    scheduleTime,
    timeZone,
    state,
    externalOrganization,
    groupId,
    status,
    loanType,
    loanNumber,
  } = room;
  const rescheduleButton = onReschedule && (
    <div className={css(styles.actionWrapper)}>
      <LinkButton
        label="Schedule"
        onClick={e => {
          e.stopPropagation();
          onReschedule();
        }}
        data-it={`link-reschedule`}
      />
    </div>
  );

  const user = useSelector((reduxState: State) => reduxState.user);

  const isSuperAdmin = permissions.includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);
  const isAdmin = isGroupAdmin(permissions);
  const isAssignedNotary = room.notaryId && user?.id === room.notaryId;

  const [allGroups, setAllGroups] = useState<NotaryAPI.GroupTuple[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState(room.groupId || '');
  const dispatch = useDispatch();

  const fetchGroups = async () => {
    try {
      if (isSuperAdmin && !allGroups.length) {
        const { groups } = await notaryAPIObj.rooms.getAllGroups();
        setAllGroups(groups);
      }
    } catch (err) {
      setAllGroups([]);
      diagnostics.error(new Error('Failed to load list of groups'), { err });
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleGroupChange = (e: any) => {
    const value = e.target.value;
    if (value === '') {
      return;
    }
    setSelectedGroupId(value);
    updateGroup(value);
  };

  const updateGroup = async (groupId: string) => {
    try {
      await notaryAPIObj.rooms.updateRoomGroup(room.id, groupId);
      onGroupOverwrite && onGroupOverwrite();
      const notifications: NotificationItem.PropsWithoutId[] = [
        {
          type: 'info',
          iconType: 'success',
          content: 'The group has been updated.',
          shouldAutoRemove: true,
        },
      ];
      addNotifications(notifications)(dispatch);
    } catch (err) {
      diagnostics.error(new Error('Failed to update group'), { err });
      onGroupOverwrite && onGroupOverwrite();
    }
  };

  /** sort the list of group alphabetical, the selected group is always displayed as first item. */
  const sortedGroups: () => NotaryAPI.GroupTuple[] = () => {
    const selectedGroupId = groupId || null;
    const alphabeticalOrder = allGroups
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(group => group.id !== selectedGroupId);
    const selectedGroup = allGroups.find(group => group.id === selectedGroupId);
    const selectedAndAlphabetical =
      selectedGroupId && alphabeticalOrder.length ? [selectedGroup, ...alphabeticalOrder] : alphabeticalOrder;
    return selectedAndAlphabetical as NotaryAPI.GroupTuple[];
  };

  const allGroupsDropdown = (
    <SelectInput
      value={selectedGroupId || groupId}
      id="groups"
      choices={sortedGroups().map(group => ({ value: group.id, text: group.name }))}
      onChange={handleGroupChange}
    />
  );

  const scheduleTimeInfo =
    scheduleTime && timeZone
      ? { rowInfo: formatTimeSlot(scheduleTime), rowAction: rescheduleButton }
      : {
          rowInfo: <Tag theme="warning" text="PENDING SCHEDULING" fontWeight="semibold" />,
        };

  const notaryInfo = notaryName
    ? {
        rowInfo: notaryName,
      }
    : {
        rowInfo: (
          <Tag
            theme="warning"
            text={status !== RoomStatus.PAUSED ? 'PENDING ASSIGNMENT' : 'NOT ASSIGNED'}
            fontWeight="semibold"
          />
        ),
      };

  const blendLoanNumberText = loanNumber ? `#${loanNumber}` : ``;
  const loanTypeAndLocation = loanType ? `${startCase(loanType)} - ${state}` : state;

  const videoDropdown = (
    <div className={css(styles.actionWrapper)}>
      <VideoProviderDropdown
        roomId={room.id}
        defaultValue={room.videoProviderOverride}
        handleChange={onVideoOverride}
        disabled={!!room.isCallInProgress}
      />
    </div>
  );

  const handleStopCall = () => {
    updateExitModalStatus(true)(dispatch);
  };

  const hasPermission = isSuperAdmin || isAdmin;
  // We should only show the stop call button if the call is active
  const isCallStoppable = callStatus && ActiveCallStatuses.includes(callStatus as CallStatus);
  const canStopCall = hasPermission && isCallStoppable;

  const stopCallButton = canStopCall ? (
    <div className={css(styles.buttonWrapper)}>
      <ActionButton
        data-dd-privacy="allow"
        data-it="button-stop-call"
        btnSize="md"
        label="Stop Call"
        type="button"
        onClick={handleStopCall}
        isFullWidth={true}
      />
    </div>
  ) : null;

  return (
    <div className={css(styles.summaryContainer)}>
      <div className={css(styles.originalContent)}>
        <div className={css(styles.summaryCard)}>
          {status && (
            <div className={css(styles.smallMargin)} data-it="RoomStatus">
              <Text color={BrandColors.NIGHT_80} fontSize="sm" weight="semibold">
                {status}
              </Text>
            </div>
          )}
          <div className={css(styles.smallMargin)}>
            <Text fontSize="md" weight="semibold">
              {signers.length > 1 ? `${signers[0]}, +${signers.length - 1} other` : signers[0]}
            </Text>
          </div>
          <div className={css(styles.largeMargin)} data-it="ExternalOrganization">
            <Text color={BrandColors.NIGHT_80} fontSize="md">
              {externalOrganization} {blendLoanNumberText}
            </Text>
          </div>
          <div className={css(styles.smallMargin)} data-it="LoanTypeAndLocation">
            <Text>{loanTypeAndLocation}</Text>
          </div>
        </div>
        <div className={css(styles.summaryCard)}>
          <div className={css(styles.largeMargin)}>
            <Text color={BrandColors.NIGHT_80} fontSize="sm" weight="semibold">
              {toUpper(`notarization info`)}
            </Text>
          </div>
          <RoomInfoRow
            iconType="calendar_today"
            rowHeader="Date"
            rowInfo={formatLocalizedScheduleDate(scheduleDate)}
            id="it-RoomInfoRow-scheduleDate"
          />
          <RoomInfoRow
            iconType="schedule"
            rowHeader="Time"
            rowInfo={scheduleTimeInfo.rowInfo}
            rowAction={scheduleTimeInfo.rowAction}
            id="it-RoomInfoRow-scheduleTime"
          />
        </div>
        <div className={css(styles.summaryCard, styles.summaryCardWithoutHeader)}>
          {(!isAssignedNotary || isSuperAdmin) && (
            <RoomInfoRow iconType="person" rowHeader="Notary" rowInfo={notaryInfo.rowInfo} id="it-RoomInfoRow-notary" />
          )}

          {isSuperAdmin && getConfig()?.enableVideoProviderDropdownForNotaries20240826 && (
            <RoomInfoRow
              iconType="videocam"
              rowHeader="Video Provider"
              rowAction={videoDropdown}
              id="it-RoomInfoRow-video-override"
            />
          )}
        </div>
        {isSuperAdmin && (
          <div className={css(styles.summaryCard, styles.summaryCardWithSelect)}>
            <RoomInfoRow iconType="groups" rowHeader="Group" rowInfo={allGroupsDropdown} id="it-RoomInfoRow-group" />
          </div>
        )}
      </div>

      {canStopCall && <div className={css(styles.summaryCard, styles.summaryCardWithSelect)}>{stopCallButton}</div>}
    </div>
  );
};
